import { AnimatePresence, motion } from 'motion/react'
import { useRef } from 'react'
import styled from 'styled-components'
import { useMemo } from 'use-memo-one'

import { WalletAddressCell } from '@root/entities/wallet/ui/table/column/wallet-address-cell'
import { WalletsBalanceCell } from '@root/entities/wallet/ui/table/column/wallet-balance-cell'
import { WalletsAssetsCell } from '@root/entities/wallet/ui/table/column/wallets-assets-cell'

import {
  HOLDINGS_ASSETS_TABLE_HEADER_HEIGHT,
  HOLDINGS_ASSETS_TABLE_ROW_HEIGHT,
  HoldingsAssetsTable,
} from './holdings-assets-table/holdings-assets-table'

import type { UserProfileHoldingsWallet } from '../model/user-profile-holdings-wallet'

const ROW_HEIGHT = 40

type Props = {
  wallet: UserProfileHoldingsWallet
  expanded: boolean
  onExpandToggle: () => void
  style: React.CSSProperties
  faded: boolean
  bodyHeight: number
}

export const HoldingsTableRow = ({
  wallet,
  expanded,
  onExpandToggle,
  style,
  faded,
  bodyHeight,
}: Props) => {
  const ref = useRef<HTMLDivElement>(null)

  const assetsCount = wallet.assets.length

  const assetsTableHeight = useMemo(() => {
    return (
      assetsCount * HOLDINGS_ASSETS_TABLE_ROW_HEIGHT +
      HOLDINGS_ASSETS_TABLE_HEADER_HEIGHT +
      1
    )
  }, [assetsCount])

  const height = expanded
    ? Math.min(assetsTableHeight + ROW_HEIGHT, bodyHeight)
    : ROW_HEIGHT

  return (
    <Root
      ref={ref}
      data-expanded={expanded}
      data-faded={faded}
      animate={{ height }}
      transition={{ duration: 0.2 }}
      onAnimationComplete={() => {
        if (expanded) {
          ref.current?.scrollIntoView({ behavior: 'smooth', block: 'nearest' })
        }
      }}
    >
      <Inner data-faded={faded}>
        <InnerContent style={style}>
          <WalletAddressCell walletAddress={wallet.address} />
          <WalletsBalanceCell
            balanceUsd={wallet.totalBalanceUsd}
            balanceNative={wallet.totalBalanceNative}
            network={wallet.network}
            iconSize={18}
            withSeparator={false}
          />
          <WalletsAssetsCell
            assetsNumber={assetsCount}
            onExpandToggle={onExpandToggle}
            expanded={expanded}
          />
        </InnerContent>
      </Inner>
      <AnimatePresence>
        {expanded ? (
          <ExpandedContent
            initial={{ opacity: 0 }}
            animate={{ opacity: 1 }}
            exit={{ opacity: 0 }}
            transition={{ duration: 0.2 }}
          >
            <HoldingsAssetsTable assets={wallet.assets} />
          </ExpandedContent>
        ) : undefined}
      </AnimatePresence>
    </Root>
  )
}

const Inner = styled.div`
  height: ${ROW_HEIGHT}px;
  font-size: 12px;

  position: relative;
  transition: background 0.2s ease-in-out;
`

const InnerContent = styled.div`
  display: grid;
  justify-items: start;
  align-items: center;
  width: 100%;
  height: 100%;
  padding-inline: 12px;
  position: relative;
`

const Root = styled(motion.div)`
  transition: opacity 0.2s linear;
  display: grid;
  grid-template-rows: auto minmax(0, 1fr);

  &:nth-child(even) {
    ${Inner} {
      background: var(--appearanceA-2, var(--appearanceA-2));
    }
  }
  &:nth-child(odd) {
    ${Inner} {
      background: var(--appearanceA-4, var(--appearanceA-4));
    }
  }
  &[data-expanded='true'] {
    background: var(--appearanceA-4, var(--appearanceA-4));
    ${Inner} {
      background: var(--appearance-1);
    }
  }
  &[data-expanded='false']:hover {
    ${Inner} {
      background: var(--backgroundA-1);
    }
  }
  &[data-faded='true'] {
    pointer-events: none;
    opacity: 0.25;
  }
`

const ExpandedContent = styled(motion.div)`
  display: grid;
`
